import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  GitBranch, ArrowLeft, ExternalLink, AlertTriangle, FileText,
  ZoomIn, ZoomOut, RotateCw, ChevronRight, Sparkles, RefreshCw,
} from "lucide-react";

import { api, formatApiErrorDetail } from "../lib/api";

/**
 * Lineage DAG page — custom SVG, no external graph lib.
 * Layout: horizontal chain (1 parent → N children), vertical fan-out when
 * a parent has multiple children. Each node is clickable and opens a side
 * inspector with quick links to /analysis/{doc_id} and /alerts?document_id=...
 *
 * Route: /lineage/:chain_root_id
 */
const NODE_WIDTH = 220;
const NODE_HEIGHT = 96;
const COL_GAP = 80;
const ROW_GAP = 24;
const PADDING = 32;

const KIND_COLORS = {
  base:        { bg: "#0F1E3D", fg: "#FFFFFF", border: "#0F1E3D", chip: "Document de base" },
  amendment:   { bg: "#FEF3C7", fg: "#92400E", border: "#FCD34D", chip: "Avenant" },
  endorsement: { bg: "#DBEAFE", fg: "#1E3A8A", border: "#93C5FD", chip: "Endossement" },
  modification:{ bg: "#F3E8FF", fg: "#6B21A8", border: "#D8B4FE", chip: "Modification" },
  renewal:     { bg: "#D1FAE5", fg: "#065F46", border: "#6EE7B7", chip: "Renouvellement" },
  restatement: { bg: "#FFE4E6", fg: "#9F1239", border: "#FDA4AF", chip: "Refonte" },
};

export default function Lineage() {
  const { chain_root_id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation("alerts");
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(true);
  const [zoom, setZoom] = useState(1);
  const [selectedNode, setSelectedNode] = useState(null);
  const [rescanning, setRescanning] = useState(null);

  const fetchGraph = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/lineage/chains/${chain_root_id}/graph`);
      setGraph(r.data);
    } catch (e) {
      const msg = formatApiErrorDetail(e.response?.data?.detail);
      toast.error(msg || "Chaîne introuvable");
      setGraph(null);
    } finally {
      setLoading(false);
    }
  }, [chain_root_id]);

  useEffect(() => { fetchGraph(); }, [fetchGraph]);

  // Compute SVG positions (depth=x, breadth=y inside each depth column)
  const layout = useMemo(() => {
    if (!graph?.nodes?.length) return null;
    const nodesByDepth = new Map();
    for (const n of graph.nodes) {
      if (!nodesByDepth.has(n.depth)) nodesByDepth.set(n.depth, []);
      nodesByDepth.get(n.depth).push(n);
    }
    const depths = Array.from(nodesByDepth.keys()).sort((a, b) => a - b);
    const maxBreadth = Math.max(...Array.from(nodesByDepth.values(), arr => arr.length));

    const positioned = {};
    let totalWidth = PADDING * 2;
    let totalHeight = PADDING * 2 + maxBreadth * NODE_HEIGHT + (maxBreadth - 1) * ROW_GAP;

    depths.forEach((depth, colIdx) => {
      const items = nodesByDepth.get(depth);
      const colHeight = items.length * NODE_HEIGHT + (items.length - 1) * ROW_GAP;
      const yStart = (totalHeight - colHeight) / 2;
      const x = PADDING + colIdx * (NODE_WIDTH + COL_GAP);
      items.forEach((node, i) => {
        positioned[node.doc_id] = {
          x,
          y: yStart + i * (NODE_HEIGHT + ROW_GAP),
          node,
        };
      });
      totalWidth = Math.max(totalWidth, x + NODE_WIDTH + PADDING);
    });

    return { positioned, totalWidth, totalHeight, depths };
  }, [graph]);

  const onForceRescan = async (childDocId) => {
    setRescanning(childDocId);
    try {
      const r = await api.post(`/documents/${childDocId}/lineage/rescan`);
      const created = r.data?.contradictions?.created || 0;
      if (created > 0) {
        toast.success(`Rescan terminé — ${created} nouvelles contradictions captées`);
      } else {
        toast.success("Rescan terminé — aucune nouvelle divergence détectée");
      }
      await fetchGraph();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Échec du rescan");
    } finally {
      setRescanning(null);
    }
  };

  if (loading) {
    return (
      <div className="animate-fade-up max-w-7xl" data-testid="lineage-page-loading">
        <div className="h-10 w-72 bg-zinc-100 rounded animate-pulse mb-6" />
        <div className="h-96 bg-zinc-50 border border-zinc-100 rounded-2xl animate-pulse" />
      </div>
    );
  }

  if (!graph) {
    return (
      <div className="animate-fade-up max-w-7xl" data-testid="lineage-page-empty">
        <button
          onClick={() => navigate(-1)}
          className="text-sm text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1 mb-4"
          data-testid="lineage-back"
        >
          <ArrowLeft size={14} /> Retour
        </button>
        <div className="text-center py-20 px-6 rounded-xl border border-dashed border-zinc-200 bg-white">
          <GitBranch className="mx-auto text-zinc-400 mb-3" size={40} strokeWidth={1.25} />
          <p className="text-sm font-medium text-zinc-700">
            Cette chaîne documentaire est introuvable ou ne contient qu&apos;un seul document.
          </p>
        </div>
      </div>
    );
  }

  const stats = graph.stats || {};
  const baseNode = graph.nodes.find((n) => n.kind === "base") || graph.nodes[0];

  return (
    <div className="animate-fade-up max-w-7xl" data-testid="lineage-page">
      {/* Header */}
      <header className="mb-5">
        <button
          onClick={() => navigate(-1)}
          className="text-xs text-zinc-500 hover:text-zinc-950 inline-flex items-center gap-1 mb-3"
          data-testid="lineage-back"
        >
          <ArrowLeft size={12} /> Retour
        </button>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-zinc-950 text-white">
                <GitBranch size={15} strokeWidth={2} />
              </span>
              <h1
                className="font-display font-semibold text-2xl tracking-tight text-zinc-950"
                data-testid="lineage-title"
              >
                Chaîne documentaire
              </h1>
            </div>
            <p className="text-sm text-zinc-500 max-w-2xl">
              <span className="font-medium text-zinc-700">{baseNode?.filename}</span>
              <span className="mx-2 text-zinc-300">·</span>
              {stats.total_nodes} document{stats.total_nodes > 1 ? "s" : ""}
              {stats.depth_max ? ` · profondeur ${stats.depth_max + 1}` : ""}
              {stats.branches_max > 1 ? ` · ${stats.branches_max} branches` : ""}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <KpiPill
              label="Changements adverses"
              value={stats.total_adverse_changes || 0}
              tone={stats.total_adverse_changes > 0 ? "rose" : "zinc"}
            />
            <KpiPill
              label="Alertes ouvertes"
              value={stats.open_alerts_count || 0}
              tone={stats.open_alerts_count > 0 ? "amber" : "zinc"}
            />
          </div>
        </div>
      </header>

      {/* Toolbar */}
      <div className="flex items-center gap-2 mb-3" data-testid="lineage-toolbar">
        <button
          onClick={() => setZoom((z) => Math.max(0.5, +(z - 0.1).toFixed(1)))}
          className="p-1.5 rounded-lg border border-zinc-200 hover:bg-zinc-50"
          data-testid="lineage-zoom-out"
        >
          <ZoomOut size={14} />
        </button>
        <button
          onClick={() => setZoom(1)}
          className="text-[11px] px-2 py-1 rounded-lg border border-zinc-200 hover:bg-zinc-50 tabular-nums"
          data-testid="lineage-zoom-reset"
        >
          {Math.round(zoom * 100)}%
        </button>
        <button
          onClick={() => setZoom((z) => Math.min(1.5, +(z + 0.1).toFixed(1)))}
          className="p-1.5 rounded-lg border border-zinc-200 hover:bg-zinc-50"
          data-testid="lineage-zoom-in"
        >
          <ZoomIn size={14} />
        </button>
        <button
          onClick={fetchGraph}
          className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-zinc-200 text-xs hover:bg-zinc-50"
          data-testid="lineage-refresh"
        >
          <RotateCw size={12} /> Rafraîchir
        </button>
      </div>

      {/* SVG canvas */}
      <div
        className="border border-zinc-200 rounded-2xl bg-gradient-to-b from-white to-zinc-50 overflow-auto"
        style={{ maxHeight: "640px" }}
        data-testid="lineage-canvas"
      >
        <svg
          width={layout.totalWidth * zoom}
          height={layout.totalHeight * zoom}
          viewBox={`0 0 ${layout.totalWidth} ${layout.totalHeight}`}
          xmlns="http://www.w3.org/2000/svg"
        >
          {/* Edges (drawn first so they sit below nodes) */}
          {graph.edges.map((e) => {
            const from = layout.positioned[e.from];
            const to = layout.positioned[e.to];
            if (!from || !to) return null;
            const x1 = from.x + NODE_WIDTH;
            const y1 = from.y + NODE_HEIGHT / 2;
            const x2 = to.x;
            const y2 = to.y + NODE_HEIGHT / 2;
            const midX = (x1 + x2) / 2;
            const path = `M ${x1} ${y1} C ${midX} ${y1}, ${midX} ${y2}, ${x2} ${y2}`;
            return (
              <g key={e.id} data-testid={`lineage-edge-${e.from}-${e.to}`}>
                <path d={path} stroke="#A1A1AA" strokeWidth="1.5" fill="none" />
                <polygon
                  points={`${x2 - 6},${y2 - 4} ${x2},${y2} ${x2 - 6},${y2 + 4}`}
                  fill="#A1A1AA"
                />
                <text
                  x={midX}
                  y={(y1 + y2) / 2 - 6}
                  fontSize="10"
                  fill="#52525B"
                  textAnchor="middle"
                  fontFamily="Helvetica,Arial,sans-serif"
                  style={{ textTransform: "uppercase", letterSpacing: "0.08em" }}
                >
                  {e.relationship_kind || "modification"}
                </text>
              </g>
            );
          })}

          {/* Nodes */}
          {graph.nodes.map((n) => {
            const pos = layout.positioned[n.doc_id];
            if (!pos) return null;
            const palette = KIND_COLORS[n.kind] || KIND_COLORS.amendment;
            const isSelected = selectedNode === n.doc_id;
            return (
              <g
                key={n.doc_id}
                transform={`translate(${pos.x}, ${pos.y})`}
                style={{ cursor: "pointer" }}
                onClick={() => setSelectedNode(n.doc_id)}
                data-testid={`lineage-node-${n.doc_id}`}
              >
                <rect
                  width={NODE_WIDTH}
                  height={NODE_HEIGHT}
                  rx="12"
                  ry="12"
                  fill={palette.bg}
                  stroke={isSelected ? "#0F1E3D" : palette.border}
                  strokeWidth={isSelected ? 2.5 : 1.5}
                />
                <text
                  x="14"
                  y="22"
                  fontSize="9"
                  fill={palette.fg}
                  fontFamily="Helvetica,Arial,sans-serif"
                  fontWeight="700"
                  style={{ textTransform: "uppercase", letterSpacing: "0.14em", opacity: 0.7 }}
                >
                  {palette.chip}
                </text>
                <foreignObject x="14" y="28" width={NODE_WIDTH - 28} height="36">
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    style={{
                      fontFamily: "Helvetica,Arial,sans-serif",
                      fontSize: "12px",
                      fontWeight: 600,
                      color: palette.fg,
                      lineHeight: "1.3",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                    title={n.filename}
                  >
                    {n.filename}
                  </div>
                </foreignObject>
                {n.adverse_change_count > 0 && (
                  <g transform={`translate(${NODE_WIDTH - 80}, ${NODE_HEIGHT - 24})`}>
                    <rect
                      width="64"
                      height="18"
                      rx="9"
                      ry="9"
                      fill="#BE123C"
                    />
                    <text
                      x="32"
                      y="12.5"
                      fontSize="10"
                      fill="#FFFFFF"
                      textAnchor="middle"
                      fontWeight="700"
                      fontFamily="Helvetica,Arial,sans-serif"
                    >
                      {n.adverse_change_count} ⚠
                    </text>
                  </g>
                )}
                <text
                  x="14"
                  y={NODE_HEIGHT - 12}
                  fontSize="9.5"
                  fill={palette.fg}
                  fontFamily="Helvetica,Arial,sans-serif"
                  style={{ opacity: 0.65 }}
                >
                  {n.document_type || "—"}
                </text>
              </g>
            );
          })}
        </svg>
      </div>

      {/* Side inspector */}
      {selectedNode && (
        <NodeInspector
          node={graph.nodes.find((n) => n.doc_id === selectedNode)}
          onClose={() => setSelectedNode(null)}
          onForceRescan={onForceRescan}
          rescanning={rescanning === selectedNode}
        />
      )}
    </div>
  );
}

function KpiPill({ label, value, tone }) {
  const cls =
    tone === "rose"  ? "bg-rose-50 border-rose-200 text-rose-900"
    : tone === "amber" ? "bg-amber-50 border-amber-200 text-amber-900"
    : "bg-zinc-50 border-zinc-200 text-zinc-700";
  return (
    <div
      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full border ${cls}`}
      data-testid={`lineage-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <span className="text-[10px] uppercase tracking-wider font-semibold opacity-70">
        {label}
      </span>
      <strong className="text-base tabular-nums">{value}</strong>
    </div>
  );
}

function NodeInspector({ node, onClose, onForceRescan, rescanning }) {
  if (!node) return null;
  const palette = KIND_COLORS[node.kind] || KIND_COLORS.amendment;
  return (
    <aside
      className="fixed right-6 bottom-6 w-96 bg-white border border-zinc-200 rounded-2xl shadow-xl p-5 z-30"
      data-testid="lineage-node-inspector"
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <p
            className="text-[10px] uppercase tracking-wider font-semibold"
            style={{ color: palette.fg, opacity: 0.85 }}
          >
            {palette.chip}
          </p>
          <p
            className="font-semibold text-zinc-950 mt-0.5 leading-tight"
            data-testid="lineage-inspector-filename"
          >
            {node.filename}
          </p>
        </div>
        <button
          onClick={onClose}
          className="text-zinc-400 hover:text-zinc-950 text-xl leading-none"
          data-testid="lineage-inspector-close"
        >
          ×
        </button>
      </div>
      <ul className="space-y-1.5 text-[12px] text-zinc-600 mb-4">
        <li>Type : <strong className="text-zinc-900">{node.document_type || "—"}</strong></li>
        <li>Profondeur : <strong className="text-zinc-900">{node.depth}</strong></li>
        {node.created_at && (
          <li>Créé : <strong className="text-zinc-900">{node.created_at.slice(0, 10)}</strong></li>
        )}
        {node.effective_date && (
          <li>Effet : <strong className="text-zinc-900">{node.effective_date.slice(0, 10)}</strong></li>
        )}
        {node.adverse_change_count > 0 && (
          <li className="text-rose-700">
            <AlertTriangle size={11} className="inline mr-1" />
            {node.adverse_change_count} changement{node.adverse_change_count > 1 ? "s" : ""} adverse{node.adverse_change_count > 1 ? "s" : ""}
          </li>
        )}
      </ul>
      <div className="flex flex-col gap-1.5">
        <Link
          to={`/analysis/${node.doc_id}`}
          className="inline-flex items-center justify-between px-3 py-2 rounded-lg bg-zinc-950 text-white text-xs hover:bg-zinc-800"
          data-testid="lineage-inspector-open-analysis"
        >
          <span className="inline-flex items-center gap-1.5"><FileText size={12} /> Voir l&apos;analyse</span>
          <ChevronRight size={12} />
        </Link>
        {node.adverse_change_count > 0 && (
          <Link
            to={`/alerts?document_id=${node.doc_id}&source_type=contradiction`}
            className="inline-flex items-center justify-between px-3 py-2 rounded-lg border border-rose-200 bg-rose-50 text-rose-900 text-xs hover:bg-rose-100"
            data-testid="lineage-inspector-open-alerts"
          >
            <span className="inline-flex items-center gap-1.5">
              <AlertTriangle size={12} /> Voir les contradictions
            </span>
            <ChevronRight size={12} />
          </Link>
        )}
        {node.kind !== "base" && (
          <button
            type="button"
            onClick={() => onForceRescan(node.doc_id)}
            disabled={rescanning}
            className="inline-flex items-center justify-between px-3 py-2 rounded-lg border border-violet-200 bg-violet-50 text-violet-900 text-xs hover:bg-violet-100 disabled:opacity-60"
            data-testid="lineage-inspector-force-rescan"
          >
            <span className="inline-flex items-center gap-1.5">
              {rescanning ? <RefreshCw size={12} className="animate-spin" /> : <Sparkles size={12} />}
              {rescanning ? "Rescan en cours…" : "Forcer le rescan"}
            </span>
            <ChevronRight size={12} />
          </button>
        )}
      </div>
    </aside>
  );
}
